import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { checkIsPlaceholder } from "../selectors/pictures.js";
import buttonStyles from "../stylesheets/components/button.module.css";
import styles from "../stylesheets/components/multi-box.module.css";
import {
  AspectRatio,
  ColorScheme,
  HighlightsItem,
  Language,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import ConditionalLink from "./ConditionalLink.js";
import CroppedImage from "./CroppedImage.js";
import HighlightHeading from "./HighlightHeading.js";
import ReadMoreCollapse from "./ReadMoreCollapse.js";

interface Props {
  item: HighlightsItem;
  scheme: ColorScheme;
  mediaAspectRatio: AspectRatio;
  isPreview: boolean;
  isPlaceholderItem: boolean;
  languageId: Language;
  collapsedLinesCount: number | undefined;
  isEditing: boolean;
  index: number;
  hasHighlightsTitle: boolean;
  isFirstOnPage: boolean;
}

const MultiBoxItem: FunctionComponent<Props> = ({
  item: {
    title,
    textAlign,
    subtitle,
    picture,
    link = {},
    text,
    imageDetail,
    buttons,
  },
  scheme,
  mediaAspectRatio,
  isPreview,
  isPlaceholderItem,
  languageId,
  collapsedLinesCount,
  isEditing,
  index,
  hasHighlightsTitle,
  isFirstOnPage,
}) => {
  const showPicture = !checkIsPlaceholder(picture) || isPlaceholderItem;

  return (
    <section
      className={ClassNames(
        styles.MultiBox__Item,
        css({ background: scheme.main.background, color: scheme.main.text }),
      )}
    >
      <div className={styles.MultiBox__Item__Inner}>
        {showPicture && (
          <div className={styles.MultiBox__Item__Picture}>
            <ConditionalLink anchorAttrs={link} isPreview={isPreview}>
              <CroppedImage
                aspectRatio={mediaAspectRatio}
                picture={picture}
                width={1280}
                detail={imageDetail}
                sizes="(min-width: 640px) 50vw, (min-width: 1024px) 33vw, (min-width: 1600px) 500px, 100vw"
              />
            </ConditionalLink>
          </div>
        )}

        <div className={styles.MultiBox__Item__Content}>
          <div>
            <HighlightHeading
              className={styles.MultiBox__Item__Header}
              title={{
                value: title,
                className: ClassNames(
                  styles.MultiBox__Item__Title,
                  `TextAlign--${textAlign.title}`,
                  css({ color: scheme.main.title }),
                ),
                children: (
                  <ConditionalLink
                    anchorAttrs={link}
                    className={styles.MultiBox__Item__HeaderLink}
                    isPreview={isPreview}
                  >
                    {title}
                  </ConditionalLink>
                ),
              }}
              subtitle={{
                value: subtitle,
                className: ClassNames(
                  styles.MultiBox__Item__Subtitle,
                  `TextAlign--${textAlign.subtitle}`,
                ),
              }}
              highlightIndex={index}
              hasHighlightsTitle={hasHighlightsTitle}
              isFirstOnPage={isFirstOnPage}
            />

            {text && (
              <div>
                {collapsedLinesCount !== undefined ? (
                  <ReadMoreCollapse
                    backgroundColor={scheme.main.background}
                    languageId={languageId}
                    collapsedLinesCount={collapsedLinesCount}
                    isEditing={isEditing}
                  >
                    {text}
                  </ReadMoreCollapse>
                ) : (
                  text
                )}
              </div>
            )}
          </div>
          <div className={styles.MultiBox__Item__Bar}>
            {buttons && (
              <div
                className={ClassNames(
                  buttonStyles.ButtonGroup,
                  buttonStyles["ButtonGroup--full-width"],
                  textAlign.buttons !== "justify"
                    ? buttonStyles[`ButtonGroup--align-${textAlign.buttons}`]
                    : undefined,
                )}
              >
                {buttons}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiBoxItem;
